/* 天賦被動（v0.5.0）：每名武將一個、不佔技能格。
 * 有專屬天賦的武將（陳到、周泰、郭脩）寫在自己的檔案；其餘武將用這裡的通用效果：
 *   名稱、種類 k、數值（陣列 = 1／7／17 級三段），說明由種類自動產生，數值改了說明跟著變。
 * 掛勾在載入時登記（core/passives.ts 載入前），heroes/index.ts 排好名冊後呼叫 applyTalents。 */
import { SG, gd } from '../core/kit.ts';
import type { Unit } from './types.ts';
import { on } from '../core/hooks.ts';

type V = number | number[];
type Tal = { k: string; [key: string]: any };

const LV = [1, 7, 17];
const pc = (v: V) => (Array.isArray(v) ? v.map((x) => +(x * 100).toFixed(1)).join('/') : String(+((v as number) * 100).toFixed(1)));
const n = (v: V) => (Array.isArray(v) ? v.join('/') : String(v));
const br = (s: string) => (s.includes('/') ? `{${s}}` : s);
const P = (v: V) => br(pc(v)) + '%';
const N = (v: V) => br(n(v));

// 各種類的說明（數值帶進去）
const DESC: Record<string, (t: Tal) => string> = {
  exec: (t) => `對生命低於 ${Math.round(t.below * 100)}% 的敵將，傷害 +${P(t.p)}。`,
  fresh: (t) => `對生命高於 ${Math.round(t.above * 100)}% 的敵將，傷害 +${P(t.p)}。`,
  lowGuard: (t) => `自己生命低於 ${Math.round(t.below * 100)}% 時，受到的傷害 -${P(t.p)}。`,
  desperate: (t) => `自己生命低於 ${Math.round(t.below * 100)}% 時，造成的傷害 +${P(t.p)}。`,
  killHeal: (t) => `參與擊殺敵將（擊殺或助攻）時，回復最大生命 ${P(t.p)}。`,
  killMana: (t) => `參與擊殺敵將（擊殺或助攻）時，回復 ${N(t.n)} 點氣。`,
  goldKill: (t) => `參與擊殺敵將（擊殺或助攻）時，多得 ${N(t.g)} 金。`,
  castHaste: (t) => `放技能後 ${t.dur} 秒內，移速 +${P(t.p)}。`,
  castShield: (t) => `放技能時得到最大生命 ${P(t.p)} 的護盾 ${t.dur} 秒（${t.cd} 秒一次）。`,
  oocRegen: (t) => `${t.after} 秒沒受到傷害後，每秒回復最大生命 ${P(t.p)}。`,
  allyGuard: (t) => `${t.r} 內的友方武將（不含自己）受到的傷害 -${P(t.p)}。`,
  siege: (t) => `對建築的傷害 +${P(t.p)}。`,
  spellVamp: (t) => `技能對敵將造成傷害的 ${P(t.p)} 轉為自己的生命（打小兵、野怪三分之一）。`,
  atkVamp: (t) => `普攻造成傷害的 ${P(t.p)} 轉為自己的生命。`,
  tenacity: (t) => `受到的硬控制時間 -${P(t.p)}。`,
  shieldLow: (t) => `生命跌破 ${Math.round(t.below * 100)}% 時，得到最大生命 ${P(t.p)} 的護盾 3 秒（${t.cd} 秒一次）。`,
  lone: (t) => `敵將身邊 ${t.r} 內沒有他的友方武將時，對他的傷害 +${P(t.p)}。`,
  ultAmp: (t) => `大招傷害 +${P(t.p)}。`,
  skillAmp: (t) => `技能傷害 +${P(t.p)}。`,
  ccAmp: (t) => `對被控制或緩速中的敵人，傷害 +${P(t.p)}。`,
  firstHit: (t) => `每 ${t.cd} 秒，對敵將的第一次普攻傷害 +${P(t.p)}。`,
  revenge: (t) => `被敵將打中後 ${t.dur} 秒內，對他的傷害 +${P(t.p)}。`,
  crowd: (t) => `${t.r} 內每有一名友方武將，造成的傷害 +${P(t.p)}（最多 3 名）。`,
  brave: (t) => `${t.r} 內每有一名敵將，造成的傷害 +${P(t.p)}（最多 3 名）。`,
  besieged: (t) => `${t.r} 內每有一名敵將，受到的傷害 -${P(t.p)}（最多 3 名）。`,
  healAmp: (t) => `受到的治療 +${P(t.p)}。`,
  petAmp: (t) => `召喚物造成的傷害 +${P(t.p)}。`,
  slowHit: (t) => `普攻打中敵將時緩速 ${P(t.p)} ${t.dur} 秒。`,
  armor: (t) => `物防 +${N(t.v)}。`,
  mr: (t) => `法防 +${N(t.v)}。`,
  cdr: (t) => `冷卻縮減 +${N(t.v)}%（上限照舊 40%）。`,
};

// 各種類的預設數值（表裡可以蓋掉）
const BASE: Record<string, object> = {
  exec: { below: 0.5, p: [0.08, 0.11, 0.14] },
  fresh: { above: 0.7, p: [0.1, 0.14, 0.18] },
  lowGuard: { below: 0.35, p: [0.1, 0.14, 0.18] },
  desperate: { below: 0.5, p: [0.08, 0.11, 0.14] },
  killHeal: { p: [0.05, 0.07, 0.09] },
  killMana: { n: [25, 40, 55] },
  goldKill: { g: [40, 60, 80] },
  castHaste: { p: [0.1, 0.12, 0.14], dur: 2 },
  castShield: { p: [0.04, 0.06, 0.08], dur: 2, cd: 10 },
  oocRegen: { p: [0.01, 0.015, 0.02], after: 5 },
  allyGuard: { r: 500, p: [0.05, 0.07, 0.09] },
  siege: { p: [0.15, 0.2, 0.25] },
  spellVamp: { p: [0.06, 0.08, 0.1] },
  atkVamp: { p: [0.05, 0.07, 0.09] },
  tenacity: { p: [0.15, 0.2, 0.25] },
  shieldLow: { below: 0.3, p: [0.1, 0.13, 0.16], cd: 60 },
  lone: { r: 600, p: [0.08, 0.11, 0.14] },
  ultAmp: { p: [0.08, 0.11, 0.14] },
  skillAmp: { p: [0.05, 0.07, 0.09] },
  ccAmp: { p: [0.08, 0.11, 0.14] },
  firstHit: { p: [0.3, 0.45, 0.6], cd: 8 },
  revenge: { p: [0.1, 0.14, 0.18], dur: 3 },
  crowd: { r: 600, p: [0.03, 0.04, 0.05] },
  brave: { r: 600, p: [0.03, 0.04, 0.05] },
  besieged: { r: 500, p: [0.04, 0.05, 0.06] },
  healAmp: { p: [0.1, 0.15, 0.2] },
  petAmp: { p: [0.1, 0.15, 0.2] },
  slowHit: { p: [0.1, 0.13, 0.16], dur: 1.5 },
  armor: { v: [1, 2, 3] },
  mr: { v: [2, 3, 4] },
  cdr: { v: [3, 4, 5] },
};

// 名冊：武將 id → [天賦名, 種類, 蓋掉的數值]
export const TALENTS: Record<string, [string, string, object?]> = {
  // ===== 蜀 =====
  liubei: ['仁德', 'allyGuard'],
  guanyu: ['武聖', 'firstHit'],
  zhangfei: ['據水斷橋', 'tenacity'],
  zhaoyun: ['一身是膽', 'besieged', { p: [0.05, 0.065, 0.08] }],
  machao: ['錦馬超', 'fresh'],
  huangzhong: ['老當益壯', 'exec'],
  zhugeliang: ['臥龍', 'cdr'],
  pangtong: ['連環計', 'ccAmp'],
  weiyan: ['反骨', 'revenge'],
  jiangwei: ['繼志', 'killMana'],
  huangyueying: ['木牛流馬', 'siege', { p: [0.05, 0.075, 0.1] }],
  madai: ['斬將', 'lone'],
  xushu: ['擊劍任俠', 'petAmp'],
  mizhu: ['富甲一方', 'goldKill'],
  menghuo: ['七擒不服', 'shieldLow'],
  zhurong: ['南蠻馴獸', 'petAmp'],
  maliang: ['白眉', 'mr'],
  fazheng: ['定軍之謀', 'ultAmp'],
  zhoucang: ['死守', 'lowGuard', { p: [0.06, 0.09, 0.12] }],
  // ===== 吳 =====
  sunquan: ['江東之主', 'crowd'], // v0.5.3 重製後改名（原「坐領江東」），效果不變
  sunce: ['小霸王', 'killHeal'],
  zhouyu: ['雅量高致', 'skillAmp'],
  luxun: ['儒生', 'castHaste'],
  lvmeng: ['襲取', 'exec'],
  // ganning：專屬天賦「錦帆賊」寫在自己的檔案（v0.5.2）
  taishici: ['神亭酣戰', 'lone', { p: [0.04, 0.06, 0.08] }],
  sunshangxiang: ['梟姬', 'atkVamp', { p: [0.07, 0.09, 0.11] }],
  daqiao: ['國色', 'allyGuard'],
  sunjian: ['討董先鋒', 'fresh'],
  xiaoqiao: ['天香', 'castShield'],
  lusu: ['指囷相贈', 'goldKill'],
  chengpu: ['程公', 'armor'],
  lingtong: ['父仇', 'revenge'],
  xusheng: ['江東虎臣', 'lowGuard'],
  dingfeng: ['短兵', 'lone'],
  // ===== 魏 =====
  caocao: ['奸雄', 'spellVamp'],
  caoren: ['據守', 'besieged'],
  xiahoudun: ['拔矢啖睛', 'atkVamp'],
  xiahouyuan: ['虎步關右', 'castHaste'],
  zhangliao: ['威震逍遙津', 'brave', { p: [0.04, 0.055, 0.07] }],
  dianwei: ['古之惡來', 'shieldLow'],
  xuchu: ['裸衣', 'firstHit'],
  xuhuang: ['長驅直入', 'siege', { p: [0.25, 0.3, 0.35] }],
  zhanghe: ['巧變', 'ccAmp'],
  yujin: ['毅重', 'petAmp'],
  yuejin: ['先登之功', 'goldKill'],
  simayi: ['鷹視狼顧', 'ultAmp', { p: [0.04, 0.06, 0.08] }],
  guojia: ['鬼才', 'cdr', { v: [5, 6, 7] }],
  xunyu: ['王佐之才', 'allyGuard'],
  jiaxu: ['亂武', 'petAmp'],
  zhenji: ['凌波微步', 'castHaste'],
  caohong: ['家富', 'goldKill'],
  zangba: ['泰山', 'tenacity', { p: [0.25, 0.3, 0.35] }],
  caozhen: ['督軍', 'crowd'],
  caoxiu: ['千里駒', 'castHaste'],
  pangde: ['抬櫬', 'desperate'],
  wenpin: ['江夏太守', 'armor'],
  zhongyao: ['墨香', 'oocRegen', { p: [0.015, 0.02, 0.025] }],
  liuye: ['霹靂車', 'siege', { p: [0.25, 0.3, 0.35] }],
  yangxiu: ['捷才', 'killMana'],
  guohuai: ['善治', 'lone'],
  dengai: ['偷渡', 'exec'],
  caozhang: ['黃鬚兒', 'fresh'],
  chengyu: ['十面埋伏', 'ccAmp'],
  xunyou: ['奇策', 'skillAmp'],
  manchong: ['持法', 'mr'],
  lidian: ['長者', 'allyGuard'],
  zhonghui: ['功高', 'goldKill'],
  caopi: ['受禪', 'ultAmp'],
  // haozhao：專屬天賦「陳倉」寫在自己的檔案（v0.5.2）
  xiahouba: ['父仇', 'revenge'],
  caochun: ['虎豹騎', 'slowHit'],
};

const BY: Record<string, Tal> = {};
// 依名冊幫沒有天賦的武將補上（武將檔自己有寫的優先）
export function applyTalents(heroes: any[]): void {
  for (const h of heroes) {
    const row = TALENTS[h.id];
    if (!row || h.passive) continue;
    const t: Tal = Object.assign({ k: row[1] }, BASE[row[1]], row[2] || {});
    BY[h.id] = t;
    const tiered = Object.values(t).some((v) => Array.isArray(v));
    h.passive = { name: row[0], desc: DESC[t.k](t), lv: tiered ? LV : undefined };
  }
}

// ---------- 效果 ----------
// SG.NO_TALENT：測其他機制的精確數值時關掉通用天賦（tests/items.cjs 等）
const tal = (u: any): Tal | null => (!SG.NO_TALENT && u && u.kind === 'hero' && u.spec ? BY[u.spec.id] || null : null);
const tier = (u: Unit) => (u.lv >= 17 ? 2 : u.lv >= 7 ? 1 : 0);
const val = (u: Unit, v: V) => (Array.isArray(v) ? v[tier(u)] : v);
const ratio = (u: Unit) => u.hp / u.maxHp;
const heroesNear = (w: any, team: number, x: number, y: number, r: number, not?: Unit) => w.units.filter((h: Unit) => h.kind === 'hero' && h.alive && h.team === team && h !== not && gd(h.x, h.y, x, y) <= r);
const isHero = (t: Unit) => t.kind === 'hero';
const ID = 'talent';

on('damage.before', 60, ID, function (this: any, c: any) {
  const own = c.own, t = c.t, o = c.o || {};
  const A = tal(own);
  if (A) {
    const p = A.p != null ? val(own, A.p) : 0;
    switch (A.k) {
      case 'exec': if (isHero(t) && ratio(t) < A.below) c.a *= 1 + p; break;
      case 'fresh': if (isHero(t) && ratio(t) > A.above) c.a *= 1 + p; break;
      case 'desperate': if (ratio(own) < A.below) c.a *= 1 + p; break;
      case 'siege': if (t.structure) c.a *= 1 + p; break;
      case 'lone': if (isHero(t) && !heroesNear(this, t.team, t.x, t.y, A.r, t).length) c.a *= 1 + p; break;
      case 'ultAmp': if (o.sk && c.act && c.act.i === 3 && c.src === own) c.a *= 1 + p; break;
      case 'skillAmp': if (o.sk && c.src === own) c.a *= 1 + p; break;
      case 'ccAmp': if (!t.structure && (this.hardCC(t) || t.st.slow > 0 || (t.bf && t.bf.ms < 0))) c.a *= 1 + p; break;
      case 'firstHit': if (o.basic && isHero(t) && !((own.talFH || 0) > this.t)) { c.a *= 1 + p; own.talFH = this.t + A.cd; } break;
      case 'revenge': if (isHero(t) && own.talRev && own.talRev[t.id] > this.t) c.a *= 1 + p; break;
      case 'crowd': c.a *= 1 + p * Math.min(3, heroesNear(this, own.team, own.x, own.y, A.r, own).length); break;
      case 'brave': c.a *= 1 + p * Math.min(3, heroesNear(this, 1 - own.team, own.x, own.y, A.r).length); break;
      case 'petAmp': if (c.src !== own && c.src.summon) c.a *= 1 + p; break;
    }
  }
  const D = tal(t);
  if (D) {
    const p = D.p != null ? val(t, D.p) : 0;
    if (D.k === 'lowGuard' && ratio(t) < D.below) c.a *= 1 - p;
    else if (D.k === 'besieged') c.a *= 1 - p * Math.min(3, heroesNear(this, 1 - t.team, t.x, t.y, D.r).length);
  }
  // 友方的「受到傷害 -X%」光環：只取最強的一個
  if (isHero(t)) {
    let best = 0;
    for (const h of this.units) {
      const G = h.kind === 'hero' && h.alive && h !== t && h.team === t.team ? tal(h) : null;
      if (G && G.k === 'allyGuard' && gd(h.x, h.y, t.x, t.y) <= G.r) best = Math.max(best, val(h, G.p));
    }
    if (best) c.a *= 1 - best;
  }
});

on('damage.after', 60, ID, function (this: any, c: any, lost: number) {
  const own = c.own, t = c.t, o = c.o || {};
  const A = tal(own);
  if (A && lost > 0) {
    if (isHero(t)) (own.talHit || (own.talHit = {}))[t.id] = this.t; // 助攻判定
    if (A.k === 'spellVamp' && o.sk && !t.structure) this.heal(own, lost * val(own, A.p) * (isHero(t) ? 1 : 1 / 3));
    else if (A.k === 'atkVamp' && o.basic && !t.structure) this.heal(own, lost * val(own, A.p));
  }
  const D = tal(t);
  if (D && t.alive) {
    if (lost > 0) t.talHurt = this.t;
    if (D.k === 'revenge' && isHero(own) && lost > 0) (t.talRev || (t.talRev = {}))[own.id] = this.t + D.dur;
    if (D.k === 'shieldLow' && ratio(t) < D.below && !((t.talSh || 0) > this.t)) {
      t.talSh = this.t + D.cd;
      t.st.shield = Math.max(t.st.shield, t.maxHp * val(t, D.p)); t.st.shieldT = Math.max(t.st.shieldT, 3);
      this.fx({ k: 'heal', x: t.x, y: t.y, col: '#ffe08a' });
      if (t.human) this.dmgEvents.push({ x: t.x, y: t.y - 90, text: t.spec.passive.name, team: t.team, src: t.id });
    }
  }
});

on('kill.after', 60, ID, function (this: any, src: any, t: any, was: boolean) {
  if (!was) return;
  const killer = src && (src.owner || src);
  for (const u of this.heroes(1 - t.team)) {
    const A = tal(u);
    if (!A || !u.alive || (A.k !== 'killHeal' && A.k !== 'killMana' && A.k !== 'goldKill')) continue;
    if (killer !== u && !(u.talHit && u.talHit[t.id] > this.t - 10)) continue;
    if (A.k === 'killHeal') this.heal(u, u.maxHp * val(u, A.p));
    else if (A.k === 'killMana') { if (u.maxMp) u.mp = Math.min(u.maxMp, u.mp + val(u, A.n)); }
    else u.gold += val(u, A.g);
  }
});

on('cast.after', 60, ID, function (this: any, u: any) {
  const A = tal(u);
  if (!A) return;
  if (A.k === 'castHaste') this.addBuff(u, { id: 'tal:haste', t: A.dur, ms: val(u, A.p) });
  else if (A.k === 'castShield' && !((u.talCs || 0) > this.t)) {
    u.talCs = this.t + A.cd;
    u.st.shield = Math.max(u.st.shield, u.maxHp * val(u, A.p)); u.st.shieldT = Math.max(u.st.shieldT, A.dur);
  }
});

on('tick', 60, ID, function (this: any, u: any, dt: number) {
  const A = tal(u);
  if (A && A.k === 'oocRegen' && u.hp < u.maxHp && this.t - (u.talHurt == null ? -99 : u.talHurt) >= A.after) u.hp = Math.min(u.maxHp, u.hp + u.maxHp * val(u, A.p) * dt);
});

on('onHit', 60, ID, function (this: any, u: any, t: any) {
  const A = tal(u);
  if (A && A.k === 'slowHit' && isHero(t) && t.alive) this.addBuff(t, { id: 'tal:slow', t: A.dur, neg: true, ms: -val(u, A.p) });
});

on('cc.tenacity', 60, ID, function (this: any, t: any) {
  const A = tal(t);
  return A && A.k === 'tenacity' ? val(t, A.p) * 100 : 0;
});

on('heal.mul', 60, ID, function (this: any, t: any, v: number) {
  const A = tal(t);
  return A && A.k === 'healAmp' ? v * (1 + val(t, A.p)) : v;
});

on('recalc', 60, ID, function (this: any, u: any) {
  const A = tal(u);
  if (!A) return;
  if (A.k === 'armor') u.armor += val(u, A.v);
  else if (A.k === 'mr') u.mr = (u.mr || 0) + val(u, A.v);
  else if (A.k === 'cdr') u.cdr = Math.min(40, (u.cdr || 0) + val(u, A.v));
});

SG.talentOf = (h: any) => BY[h && h.id] || null;
